import { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/api/auth'
import { LoginPage } from '@/pages/auth/LoginPage'
import { AppLayout } from '@/components/layout/AppLayout'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { EventListPage } from '@/pages/events/EventListPage'
import { EventCreatePage } from '@/pages/events/EventCreatePage'
import { EventDetailPage } from '@/pages/events/EventDetailPage'
import { EventPage } from '@/pages/public/EventPage'
import { TicketPage } from '@/pages/public/TicketPage'
import { PaymentReturnPage } from '@/pages/public/PaymentReturnPage'
import { TicketListPage } from '@/pages/tickets/TicketListPage'
import { UserListPage } from '@/pages/users/UserListPage'

function AuthGate({ children }: { children: React.ReactNode }) {
  const { setAuth, logout, hydrate } = useAuthStore()
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => { hydrate() }, [hydrate])

  useEffect(() => {
    if (!localStorage.getItem('auth_token')) { setIsChecking(false); return }
    authApi.me()
      .then((user) => setAuth(localStorage.getItem('auth_token')!, user))
      .catch(() => logout())
      .finally(() => setIsChecking(false))
  }, [])

  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg className="w-6 h-6 animate-spin text-primary-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    )
  }
  return <>{children}</>
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (user?.role === 'vendor') return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Routes publiques acheteur — aucune auth requise */}
        <Route path="/e/:slug" element={<EventPage />} />
        <Route path="/tickets/:uuid" element={<TicketPage />} />
        <Route path="/payment/return" element={<PaymentReturnPage />} />

        {/* Routes back office — AuthGate vérifie le token */}
        <Route
          path="/*"
          element={
            <AuthGate>
              <Routes>
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

                <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/events" element={<EventListPage />} />
                  <Route path="/events/new" element={<EventCreatePage />} />
                  <Route path="/events/:id" element={<EventDetailPage />} />
                  <Route path="/tickets" element={<TicketListPage />} />
                  <Route path="/users" element={<UserListPage />} />
                </Route>

                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AuthGate>
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
